import {
  CellMeasurer,
  CellMeasurerCache,
  List,
  AutoSizer,
  InfiniteLoader,
} from 'react-virtualized';
import { useChatContext } from '@/contexts/chat/context';
import type { User } from '@/configs/apis/user';
import { ChatCategory, type Chat } from '@/contexts/chat/scripts/hooks/useChats/types';
import useContextMenu from '@/scripts/hooks/useContextMenu';
import type { MenuAreaRef } from '@/components/contextMenu/MenuArea';
import {
  ChatGroupMemberRemoved,
  type ChatGroupMemberParamsArgs,
} from '@/contexts/chat/scripts/hooks/useChatGroup/types';
import {
  GroupPosition,
  type GroupEncryptionStatus,
} from '@/configs/apis/groups/interface';
import MessageItem from './components/messageItem';
import styles from './index.module.scss';
import SelectContact from './components/select-contact';
import ChatContextMenu from './components/chatContextMenu';

// const PAGE_SIZE = 20;
const PAGE_SIZE = 100000; // all data is -1

const MessageList = ({ user }: { user: User }) => {
  const {
    chatsState,
    singleEncryption,
    activeChat,
    createChat,
    getChat,
    loadChats,
    queryGroupMediaCalling,
    chatGroup: { insertMembers },
  } = useChatContext();
  const activatedChatId = chatsState.activatedChatId;
  const dataList = chatsState.chats;
  const pageCurrent = useRef(0);
  const isRendered = useRef(false);

  const menuAreaRef = useRef<MenuAreaRef>(null);
  const { visible, points, onContextMenu } = useContextMenu({
    contextmenuRef: (menuAreaRef.current as MenuAreaRef)?.menuAreaRef,
  });
  const [curChat, setCurChat] = useState({} as Chat);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  const cacheRef = useRef(
    new CellMeasurerCache({
      defaultHeight: 48,
      fixedWidth: true,
      minHeight: 48,
    })
  );

  const handleClick = useCallback(
    async (item: Chat) => {
      if (item.category === ChatCategory.SINGLE) {
        const packageKey = await singleEncryption.getTargetChatPubKeys(
          item.user.userId
        );
        if (!packageKey) {
          message.error('Failed to open chat');
          return;
        }
      }
      activeChat(item.chat_id);
    },
    [activeChat, singleEncryption]
  );

  const queryList = useCallback(
    async (limit: number, offset: number) => {
      if (!hasMore || loading) return Promise.resolve();

      try {
        setLoading(true);
        const chats = await loadChats(user, limit, offset);
        pageCurrent.current += 1;
        setHasMore(chats?.length >= PAGE_SIZE);
        queryGroupMediaCalling(chats);
      } catch (error) {
        console.error('Failed to load chats:', error);
      } finally {
        setLoading(false);
      }
    },
    [hasMore, loadChats, loading, queryGroupMediaCalling, user]
  );

  const rowCount = hasMore ? dataList.length + PAGE_SIZE : dataList.length;

  const isRowLoaded = ({ index }: { index: number }) => index < dataList.length;

  const loadMoreRows = () => queryList(PAGE_SIZE, pageCurrent.current * PAGE_SIZE);

  const rowRenderer = useCallback(
    ({ index, parent, style, columnIndex }: any) => {
      const item = dataList[index];
      if (!item) return null;
      return (
        <CellMeasurer
          key={`cell-${item.chat_id}`}
          cache={cacheRef.current}
          columnIndex={columnIndex}
          parent={parent}
          rowIndex={index}
        >
          {({ registerChild }) => (
            <div
              ref={registerChild}
              data-index={index}
              style={style}
              onContextMenu={(e) => {
                setCurChat(item);
                onContextMenu(e);
              }}
            >
              <MessageItem
                key={`message-${item.chat_id}`}
                chat={item}
                activatedChatId={activatedChatId}
                onClick={() => handleClick(item)}
              />
            </div>
          )}
        </CellMeasurer>
      );
    },
    [activatedChatId, dataList, handleClick, onContextMenu]
  );

  const handleActiveChat = useCallback(async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const chat_id = urlParams.get('chat_id');
    const isEncryption = (urlParams.get('isEncryption') || 0) as GroupEncryptionStatus;
    if (chat_id) {
      const chat = await getChat({ chat_id: chat_id });
      if (!chat) {
        if (chat_id.startsWith('GN_')) {
          const params: ChatGroupMemberParamsArgs[] = [
            {
              id: chat_id,
              is_encryption: isEncryption,
              args: [
                {
                  user_id: user.userId,
                  avatar: user.avatar,
                  name: user.name,
                  username: user.username,
                  create_at: user.createdAt ?? 0,
                  update_at: user.updatedAt ?? 0,
                  remark_name: '',
                  category: GroupPosition.Member,
                  role: GroupPosition.Member,
                  removed: ChatGroupMemberRemoved.Normal,
                },
              ],
            },
          ];
          console.log('========handleActiveChat params', params);
          // TODO: joinGroup
          return message.error('coming soon.');
          await insertMembers(params);
        } else {
          await createChat({ userId: user?.userId });
        }
      }
      const timerId = setTimeout(() => {
        clearTimeout(timerId);
        activeChat(chat_id);
      }, 2000);
    }
  }, [activeChat, createChat, getChat, insertMembers, user]);

  useEffect(() => {
    if (!isRendered.current) {
      handleActiveChat();
    }
    isRendered.current = true;
  }, [handleActiveChat]);

  return (
    <div className={styles.messageList}>
      <AutoSizer>
        {({ width, height }) => (
          <InfiniteLoader
            isRowLoaded={isRowLoaded}
            loadMoreRows={loadMoreRows}
            rowCount={rowCount}
            threshold={20}
          >
            {({ onRowsRendered, registerChild }) => (
              <List
                className="scrollbar_hover"
                ref={registerChild}
                width={width}
                height={height}
                deferredMeasurementCache={cacheRef.current}
                rowHeight={cacheRef.current.rowHeight}
                rowCount={rowCount}
                rowRenderer={rowRenderer}
                onRowsRendered={onRowsRendered}
                noRowsRenderer={() => <SelectContact />}
                scrollToIndex={0}
              />
            )}
          </InfiniteLoader>
        )}
      </AutoSizer>

      <ChatContextMenu
        chat={curChat}
        menuAreaRef={menuAreaRef}
        visible={visible}
        points={points}
      ></ChatContextMenu>
    </div>
  );
};

export default MessageList;
